
import React, { useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import api from "./api/axios";
import "./pdf-yearbook-import.css";

const API_URL =
  process.env.NODE_ENV === "development"
    ? "http://localhost:5000"
    : "https://api.yearbookhub.online";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

const PAGE_WIDTH = 794;
const PAGE_HEIGHT = 1123;
const MAX_PAGES = 150;
const MAX_PDF_SIZE = 50 * 1024 * 1024;

function PdfYearbookImport({ onClose, onImportPages }) {
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const selectFile = (selectedFile) => {
    setError("");
    setStatus("");

    if (!selectedFile) return;

    if (
      selectedFile.type !== "application/pdf" &&
      !selectedFile.name.toLowerCase().endsWith(".pdf")
    ) {
      setError("Please select a PDF file.");
      return;
    }

    if (selectedFile.size > MAX_PDF_SIZE) {
      setError("The PDF must be 50 MB or smaller.");
      return;
    }

    setFile(selectedFile);
  };

  const renderPdfPage = async (pdfPage) => {
    const originalViewport = pdfPage.getViewport({ scale: 1 });

    const scale = Math.min(
      PAGE_WIDTH / originalViewport.width,
      PAGE_HEIGHT / originalViewport.height
    );

    const viewport = pdfPage.getViewport({ scale });

    const canvas = document.createElement("canvas");
    canvas.width = PAGE_WIDTH;
    canvas.height = PAGE_HEIGHT;

    const context = canvas.getContext("2d", {
      alpha: false,
    });

    if (!context) {
      throw new Error("Unable to create the page preview.");
    }

    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, PAGE_WIDTH, PAGE_HEIGHT);

    const offsetX = (PAGE_WIDTH - viewport.width) / 2;
    const offsetY = (PAGE_HEIGHT - viewport.height) / 2;

    await pdfPage.render({
      canvasContext: context,
      viewport,
      transform: [1, 0, 0, 1, offsetX, offsetY],
      background: "#ffffff",
    }).promise;

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (result) => {
          if (result) resolve(result);
          else reject(new Error("Failed to render a PDF page."));
        },
        "image/jpeg",
        0.94
      );
    });

    canvas.width = 1;
    canvas.height = 1;

    return blob;
  };

  const uploadPage = async (blob, pageNumber) => {
    const formData = new FormData();

    formData.append(
      "image",
      blob,
      `yearbook-page-${pageNumber}.jpg`
    );

    const response = await api.post(
      `${API_URL}/api/upload`,
      formData
    );

    const imageUrl = response.data?.imageUrl;

    if (!imageUrl || !/^https?:\/\//i.test(imageUrl)) {
      throw new Error(
        `The upload for page ${pageNumber} did not return a valid image URL.`
      );
    }

    return imageUrl;
  };

  const handleImport = async (event) => {
    event.preventDefault();

    if (busy) return;

    if (!file) {
      setError("Please choose a PDF file first.");
      return;
    }

    setBusy(true);
    setError("");
    setProgress(0);

    let pdf = null;

    try {
      setStatus("Reading your PDF...");

      const data = await file.arrayBuffer();

      pdf = await pdfjsLib.getDocument({
        data: new Uint8Array(data),
      }).promise;

      if (pdf.numPages < 1 || pdf.numPages > MAX_PAGES) {
        throw new Error(
          `The PDF must contain between 1 and ${MAX_PAGES} pages.`
        );
      }

      const pages = new Array(pdf.numPages);

      // Convert and upload three pages at a time.
      for (let start = 1; start <= pdf.numPages; start += 3) {
        const end = Math.min(start + 2, pdf.numPages);

        setStatus(
          `Converting pages ${start}–${end} of ${pdf.numPages}...`
        );

        await Promise.all(
          Array.from(
            { length: end - start + 1 },
            (_, offset) => start + offset
          ).map(async (number) => {
            const pdfPage = await pdf.getPage(number);

            try {
              const blob = await renderPdfPage(pdfPage);

              setStatus(
                `Uploading page ${number} of ${pdf.numPages}...`
              );

              const imageUrl = await uploadPage(blob, number);

              pages[number - 1] = {
                id: number,
                title: number === 1 ? "Cover" : `Page ${number}`,
                type: "pdf-image",
                orientation: "portrait",
                imageUrl,
                preview: imageUrl,
                json: null,
              };

              setProgress(
                Math.round((number / pdf.numPages) * 90)
              );
            } finally {
              pdfPage.cleanup();
            }
          })
        );
      }

      setStatus("Loading pages into Design Studio...");

      // No draft is created here. Return pages to the main canvas.
      const result = await onImportPages(pages);

      if (result === false) {
        setStatus("");
        return;
      }

      setProgress(100);
      setStatus("All PDF pages have been loaded.");

      onClose?.();
    } catch (err) {
      console.error("PDF import failed:", err);

      setError(
        err.response?.data?.message ||
          err.message ||
          "Unable to import this PDF."
      );
    } finally {
      if (pdf) {
        try {
          await pdf.destroy();
        } catch (cleanupError) {
          console.warn("PDF cleanup warning:", cleanupError);
        }
      }

      setBusy(false);
    }
  };

  return (
    <div className="pdf-import-overlay">
      <form
        className="pdf-import-panel"
        onSubmit={handleImport}
      >
        <div className="pdf-import-header">
          <span className="pdf-import-eyebrow">
            DESIGN STUDIO
          </span>

          <button
            type="button"
            className="pdf-import-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close PDF import"
          >
            ×
          </button>
        </div>

        <label className="pdf-upload-zone">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            hidden
            disabled={busy}
            onChange={(event) =>
              selectFile(event.target.files?.[0])
            }
          />

          <span className="pdf-upload-icon">PDF</span>

          <strong>
            {file ? file.name : "Choose your yearbook PDF"}
          </strong>

          <span>
            {file
              ? `${(file.size / 1024 / 1024).toFixed(2)} MB`
              : "PDF only · Maximum 50 MB"}
          </span>

          <span className="pdf-upload-action">
            Browse files
          </span>
        </label>

        {busy && (
          <div className="pdf-import-progress">
            <div className="pdf-import-progress-track">
              <div
                className="pdf-import-progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>

            <p>{status}</p>
            <span>{progress}%</span>
          </div>
        )}

        {error && (
          <p className="pdf-import-error" role="alert">
            {error}
          </p>
        )}

        <div className="pdf-import-footer">
          <button
            type="button"
            className="pdf-import-cancel"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="pdf-import-submit"
            disabled={busy || !file}
          >
            {busy ? "Importing..." : "Import to Canvas"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default PdfYearbookImport;
